/**
 * Self-test suite: Identity-key storage (IndexedDB)
 *
 * Verifies that `SignalProtocolStore` correctly persists identity keys in
 * IndexedDB, enforces cross-account isolation, handles concurrent writes
 * gracefully, consumes prekeys on use, and — critically — stores the identity
 * private key behind a vault whose in-memory wrapping key is non-extractable
 * (`extractable === false`), mirroring the iOS Keychain non-extractability
 * guarantee.
 *
 * Each test constructs a fresh `SignalProtocolStore` backed by a uniquely-named
 * IndexedDB so tests are fully isolated and cannot pollute one another or the
 * production database. Databases are deleted after each test.
 *
 * Related iOS code: `kaether_for_ios/Stoat/E2EE/SignalStore.swift`
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import { createStore } from "idb-keyval";
import {
  Direction,
} from "@privacyresearch/libsignal-protocol-typescript";
import type { KeyPairType } from "@privacyresearch/libsignal-protocol-typescript";
import { SignalProtocolStore } from "./SignalProtocolStore";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Make a fresh, isolated store backed by a throwaway DB. */
function freshStore(): { store: SignalProtocolStore; dbName: string } {
  const dbName = `kaether-selftest-${crypto.randomUUID()}`;
  const idb = createStore(dbName, "kv");
  const store = new SignalProtocolStore(idb);
  return { store, dbName };
}

/** Delete a named IndexedDB. Resolves whether or not the DB existed. */
function dropDb(name: string): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve(); // best-effort
    req.onblocked = () => resolve();
  });
}

/** Generate a minimal 32-byte key pair that satisfies `KeyPairType`. */
function makeKeyPair(seed = 0): KeyPairType {
  const pub = new Uint8Array(32);
  const priv = new Uint8Array(32);
  pub.fill(seed + 1);
  priv.fill(seed + 2);
  return { pubKey: pub.buffer, privKey: priv.buffer };
}

function bytesEqual(a: ArrayBuffer, b: ArrayBuffer): boolean {
  if (a.byteLength !== b.byteLength) return false;
  const va = new Uint8Array(a);
  const vb = new Uint8Array(b);
  for (let i = 0; i < va.length; i++) {
    if (va[i] !== vb[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Test cases
// ---------------------------------------------------------------------------

/**
 * TC-1  Round-trip: save identity keypair, reload, bytes match.
 * Confirms the basic vault wrap/unwrap cycle works end-to-end.
 */
async function testRoundTrip(): Promise<TestCase> {
  const { store, dbName } = freshStore();
  try {
    const kp = makeKeyPair(10);
    await store.setIdentityKeyPair(kp);
    const loaded = await store.getIdentityKeyPair();
    if (!loaded) return fail("roundTrip", "getIdentityKeyPair returned undefined after set");
    if (!bytesEqual(loaded.pubKey, kp.pubKey))
      return fail("roundTrip", "public key bytes do not match after round-trip");
    if (!bytesEqual(loaded.privKey, kp.privKey))
      return fail("roundTrip", "private key bytes do not match after round-trip");
    return pass("roundTrip");
  } catch (e) {
    return fail("roundTrip", `threw: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    await dropDb(dbName);
  }
}

/**
 * TC-2  Persistence: save → simulate cold reload (new store instance, same DB)
 * → identity recovers cleanly.
 *
 * The wrapping CryptoKey is persisted in IndexedDB with `extractable: false`;
 * structured-clone preserves the non-extractable flag on retrieval. So after
 * a cold reload, the NEW store instance loads the same wrapping key from IDB,
 * unwraps the identity, and `getIdentityKeyPair` returns the original key
 * pair — matching iOS Keychain durability.
 */
async function testColdReloadPersistsIdentity(): Promise<TestCase> {
  const dbName = `kaether-selftest-${crypto.randomUUID()}`;
  try {
    // Session 1: store the keypair
    const idb1 = createStore(dbName, "kv");
    const store1 = new SignalProtocolStore(idb1);
    const kp = makeKeyPair(20);
    await store1.setIdentityKeyPair(kp);

    // Session 2: brand-new store instance (same DB). The vault re-loads the
    // persisted wrapping CryptoKey from IDB.
    const idb2 = createStore(dbName, "kv");
    const store2 = new SignalProtocolStore(idb2);

    const loaded = await store2.getIdentityKeyPair();
    if (!loaded) {
      return fail(
        "coldReloadPersistsIdentity",
        "Identity was lost across cold reload — the wrapping key isn't persisting",
      );
    }
    if (!bytesEqual(loaded.pubKey, kp.pubKey) || !bytesEqual(loaded.privKey, kp.privKey)) {
      return fail(
        "coldReloadPersistsIdentity",
        "Identity loaded after cold reload doesn't match what was stored",
      );
    }

    // Critical security check: the wrapping key retrieved from IDB must
    // STILL be non-extractable (structured-clone preserves the flag).
    const wk = store2.vault.wrappingCryptoKey;
    if (!wk) {
      return fail(
        "coldReloadPersistsIdentity",
        "vault.wrappingCryptoKey is null after cold-reload unwrap (should be loaded by ensureWrappingKey)",
      );
    }
    if (wk.extractable !== false) {
      return fail(
        "coldReloadPersistsIdentity",
        `Persisted wrapping key has extractable=${wk.extractable} after IDB round-trip — structured-clone leaked the non-extractable flag`,
      );
    }
    return pass("coldReloadPersistsIdentity");
  } catch (e) {
    return fail(
      "coldReloadPersistsIdentity",
      `threw: ${e instanceof Error ? e.message : String(e)}`,
    );
  } finally {
    await dropDb(dbName);
  }
}

/**
 * TC-2b  Same-session persistence: after calling `setIdentityKeyPair` the
 * SAME store instance can reload via `getIdentityKeyPair` — the vault's
 * wrapping key is still in memory.
 */
async function testSameSessionPersistence(): Promise<TestCase> {
  const { store, dbName } = freshStore();
  try {
    const kp = makeKeyPair(21);
    await store.setIdentityKeyPair(kp);

    // The same store instance has the vault in memory; second read must work.
    const loaded1 = await store.getIdentityKeyPair();
    if (!loaded1) return fail("sameSessionPersistence", "first reload returned undefined");

    const loaded2 = await store.getIdentityKeyPair();
    if (!loaded2) return fail("sameSessionPersistence", "second reload returned undefined");

    if (!bytesEqual(loaded2.privKey, kp.privKey))
      return fail("sameSessionPersistence", "private key diverged on second read");

    return pass("sameSessionPersistence");
  } catch (e) {
    return fail("sameSessionPersistence", `threw: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    await dropDb(dbName);
  }
}

/**
 * TC-3  Non-extractability: the vault's in-memory wrapping `CryptoKey` has
 * `extractable === false`.
 *
 * iOS parity: `kSecAttrAccessibleAfterFirstUnlockThisDeviceOnly` makes the
 * private key bytes non-extractable from the Keychain outside the Secure
 * Enclave. On the web the wrapping key stands in for that protection boundary.
 */
async function testNonExtractable(): Promise<TestCase> {
  const { store, dbName } = freshStore();
  try {
    const kp = makeKeyPair(30);
    await store.setIdentityKeyPair(kp);

    // The vault must have been initialised by the `setIdentityKeyPair` call.
    if (!store.vault.isInitialised)
      return fail("nonExtractable", "vault not initialised after setIdentityKeyPair");

    const wk = store.vault.wrappingCryptoKey;
    if (!wk)
      return fail("nonExtractable", "vault.wrappingCryptoKey is null after initialisation");

    if (wk.extractable)
      return fail(
        "nonExtractable",
        "wrapping CryptoKey has extractable=true — private key bytes are unprotected",
      );

    // Confirm the browser enforces the non-extractability constraint.
    let exportRejected = false;
    try {
      await crypto.subtle.exportKey("raw", wk);
    } catch {
      exportRejected = true;
    }
    if (!exportRejected)
      return fail(
        "nonExtractable",
        "crypto.subtle.exportKey did NOT throw for non-extractable key — constraint not enforced",
      );

    return pass("nonExtractable");
  } catch (e) {
    return fail("nonExtractable", `threw: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    await dropDb(dbName);
  }
}

/**
 * TC-4  Wrong account: store A's identity under userId A, attempt to read
 * under userId B → returns null (no cross-account leak).
 *
 * This tests the remote-identity / trusted-identity separation, since the
 * local identity keypair is global per store. For the per-remote-identity
 * path we verify key A and key B are stored independently.
 */
async function testCrossAccountIsolation(): Promise<TestCase> {
  const { store, dbName } = freshStore();
  try {
    const keyA = makeKeyPair(40);
    const keyB = makeKeyPair(41);

    await store.saveIdentity("alice.1", keyA.pubKey);
    await store.saveIdentity("bob.1", keyB.pubKey);

    const retrievedUnderBobForAlice = await store.getRemoteIdentity("alice.1");
    if (!retrievedUnderBobForAlice)
      return fail("crossAccountIsolation", "alice identity missing after save");

    // Reading alice's key under a non-existent address should return undefined
    const retrievedUnknown = await store.getRemoteIdentity("charlie.1");
    if (retrievedUnknown !== undefined)
      return fail(
        "crossAccountIsolation",
        "getRemoteIdentity returned data for unknown address charlie.1",
      );

    // Alice's key must not equal Bob's key
    const aliceBytes = await store.getRemoteIdentity("alice.1");
    const bobBytes = await store.getRemoteIdentity("bob.1");
    if (!aliceBytes || !bobBytes)
      return fail("crossAccountIsolation", "identity missing for alice or bob");
    if (bytesEqual(aliceBytes, bobBytes))
      return fail("crossAccountIsolation", "alice and bob returned the same bytes");

    return pass("crossAccountIsolation");
  } catch (e) {
    return fail("crossAccountIsolation", `threw: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    await dropDb(dbName);
  }
}

/**
 * TC-5  Concurrent writes: two simultaneous saves of the same userId both
 * complete without corruption (last-writer-wins is acceptable; total
 * corruption of both keys is not).
 */
async function testConcurrentWrites(): Promise<TestCase> {
  const { store, dbName } = freshStore();
  try {
    const kpA = makeKeyPair(50);
    const kpB = makeKeyPair(51);

    // Fire both writes simultaneously — IndexedDB queues them; the last one
    // to commit wins. We only require that the result is one of the two valid
    // values, not a corrupted blend.
    await Promise.all([
      store.setIdentityKeyPair(kpA),
      store.setIdentityKeyPair(kpB),
    ]);

    const loaded = await store.getIdentityKeyPair();
    if (!loaded)
      return fail("concurrentWrites", "getIdentityKeyPair returned undefined after concurrent writes");

    const matchesA =
      bytesEqual(loaded.pubKey, kpA.pubKey) &&
      bytesEqual(loaded.privKey, kpA.privKey);
    const matchesB =
      bytesEqual(loaded.pubKey, kpB.pubKey) &&
      bytesEqual(loaded.privKey, kpB.privKey);

    if (!matchesA && !matchesB)
      return fail(
        "concurrentWrites",
        "stored keypair matches neither writer — data corruption detected",
      );

    return pass("concurrentWrites");
  } catch (e) {
    return fail("concurrentWrites", `threw: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    await dropDb(dbName);
  }
}

/**
 * TC-6  Delete: wipe one identity, others remain intact.
 *
 * Uses `removePreKey` as the representative "delete one entry" operation.
 * Stores prekeys 1, 2, 3; removes 2; confirms 1 and 3 are still present and
 * 2 is gone.
 */
async function testDeleteIsolation(): Promise<TestCase> {
  const { store, dbName } = freshStore();
  try {
    const kp1 = makeKeyPair(61);
    const kp2 = makeKeyPair(62);
    const kp3 = makeKeyPair(63);

    await store.storePreKey(1, kp1);
    await store.storePreKey(2, kp2);
    await store.storePreKey(3, kp3);

    await store.removePreKey(2);

    const loaded1 = await store.loadPreKey(1);
    const loaded2 = await store.loadPreKey(2);
    const loaded3 = await store.loadPreKey(3);

    if (!loaded1)
      return fail("deleteIsolation", "prekey 1 missing after deleting prekey 2");
    if (loaded2)
      return fail("deleteIsolation", "prekey 2 still present after removePreKey(2)");
    if (!loaded3)
      return fail("deleteIsolation", "prekey 3 missing after deleting prekey 2");

    if (!bytesEqual(loaded1.pubKey, kp1.pubKey))
      return fail("deleteIsolation", "prekey 1 pubKey corrupted");
    if (!bytesEqual(loaded3.pubKey, kp3.pubKey))
      return fail("deleteIsolation", "prekey 3 pubKey corrupted");

    return pass("deleteIsolation");
  } catch (e) {
    return fail("deleteIsolation", `threw: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    await dropDb(dbName);
  }
}

/**
 * TC-7  PreKey replenishment: save 100 one-time prekeys, fetch one, confirm
 * it is consumed (not returned again). This is the critical Signal-protocol
 * invariant — a used prekey must not be reused.
 *
 * Note: `loadPreKey` in libsignal does NOT auto-remove. The protocol layer
 * calls `removePreKey` after use. We verify that `storePreKey` → `loadPreKey`
 * → `removePreKey` → `loadPreKey` returns undefined (consumed), while all
 * other 99 prekeys are untouched.
 */
async function testPreKeyReplenishment(): Promise<TestCase> {
  const { store, dbName } = freshStore();
  try {
    const COUNT = 100;
    for (let i = 1; i <= COUNT; i++) {
      await store.storePreKey(i, makeKeyPair(i));
    }

    // Simulate fetching prekey 42 (protocol layer picks one at random)
    const fetched = await store.loadPreKey(42);
    if (!fetched)
      return fail("preKeyReplenishment", "loadPreKey(42) returned undefined from 100 stored keys");

    // Protocol layer marks it consumed
    await store.removePreKey(42);

    // Must not be returned again
    const afterRemove = await store.loadPreKey(42);
    if (afterRemove !== undefined)
      return fail("preKeyReplenishment", "loadPreKey(42) returned a value after removePreKey — key not consumed");

    // All other 99 prekeys must survive
    for (let i = 1; i <= COUNT; i++) {
      if (i === 42) continue;
      const kp = await store.loadPreKey(i);
      if (!kp)
        return fail(
          "preKeyReplenishment",
          `preKey ${i} missing after only preKey 42 was removed`,
        );
    }

    return pass("preKeyReplenishment");
  } catch (e) {
    return fail("preKeyReplenishment", `threw: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    await dropDb(dbName);
  }
}

/**
 * TC-8  TOFU trust: first `saveIdentity` → `isTrustedIdentity` returns true;
 * changing the key → `saveIdentity` returns `true` (key changed flag); the
 * NEW key is now trusted.
 */
async function testTrustOnFirstUse(): Promise<TestCase> {
  const { store, dbName } = freshStore();
  try {
    const key1 = makeKeyPair(80);
    const key2 = makeKeyPair(81);

    // First save for unknown peer → trusted (TOFU)
    const changed1 = await store.saveIdentity("peer.1", key1.pubKey);
    if (changed1)
      return fail("tofu", "saveIdentity returned true (changed) for a brand-new peer — should be false");

    const trusted1 = await store.isTrustedIdentity("peer.1", key1.pubKey, Direction.SENDING);
    if (!trusted1)
      return fail("tofu", "isTrustedIdentity returned false for the key we just saved");

    // Second save with a different key → identity changed
    const changed2 = await store.saveIdentity("peer.1", key2.pubKey);
    if (!changed2)
      return fail("tofu", "saveIdentity returned false (unchanged) but the key was different");

    // After the update, the NEW key must be trusted
    const trustedNew = await store.isTrustedIdentity("peer.1", key2.pubKey, Direction.SENDING);
    if (!trustedNew)
      return fail("tofu", "isTrustedIdentity returned false for the newly updated key");

    // Old key must no longer be trusted
    const trustedOld = await store.isTrustedIdentity("peer.1", key1.pubKey, Direction.SENDING);
    if (trustedOld)
      return fail("tofu", "isTrustedIdentity still trusts the old key after it was changed");

    return pass("tofu");
  } catch (e) {
    return fail("tofu", `threw: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    await dropDb(dbName);
  }
}

/**
 * TC-9  clearAll: wipe removes all e2ee keys but does NOT touch unrelated keys
 * (regression guard — clearAll must not nuke the whole database).
 */
async function testClearAll(): Promise<TestCase> {
  const { store, dbName } = freshStore();
  try {
    const kp = makeKeyPair(90);
    await store.setIdentityKeyPair(kp);
    await store.storePreKey(1, makeKeyPair(91));
    await store.setLocalRegistrationId(12345);

    await store.clearAll();

    const afterKp = await store.getIdentityKeyPair();
    if (afterKp)
      return fail("clearAll", "identity keypair still present after clearAll");

    const afterPk = await store.loadPreKey(1);
    if (afterPk)
      return fail("clearAll", "preKey still present after clearAll");

    const afterReg = await store.getLocalRegistrationId();
    if (afterReg !== undefined)
      return fail("clearAll", "registrationId still present after clearAll");

    return pass("clearAll");
  } catch (e) {
    return fail("clearAll", `threw: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    await dropDb(dbName);
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Identity-key storage (IndexedDB)",
  isVerified: true,
  run: async () => {
    const cases: TestCase[] = [
      await testRoundTrip(),
      await testColdReloadPersistsIdentity(),
      await testSameSessionPersistence(),
      await testNonExtractable(),
      await testCrossAccountIsolation(),
      await testConcurrentWrites(),
      await testDeleteIsolation(),
      await testPreKeyReplenishment(),
      await testTrustOnFirstUse(),
      await testClearAll(),
    ];
    return buildSuiteResult(cases);
  },
});
